<script setup lang="ts">
import {onMounted, onBeforeUnmount, ref, computed} from 'vue'
import WaveSurfer from 'wavesurfer.js'
import {Play, Stop} from "utruckin-icons";
import {playOnlyOne, clearActiveWaveSurfer} from '@/utils/audioManager'

const props = defineProps<{
  audioUrl: string
}>()

const waveRef = ref<HTMLElement | null>(null)
const isPlaying = ref(false)
const currentTime = ref(0)
const duration = ref(0)

let wavesurfer: WaveSurfer | null = null

const togglePlay = async (event: any) => {
  if (!wavesurfer) return
  event.stopPropagation()
  if (wavesurfer.isPlaying()) {
    wavesurfer.pause()
  } else {
    playOnlyOne(wavesurfer)
  }
}

const formatTime = (seconds: number) => {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`
}

const formattedTime = computed(() => {
  return formatTime(isPlaying.value ? currentTime.value : duration.value || 0)
})

onMounted(() => {
  if (!waveRef.value) return

  wavesurfer = WaveSurfer.create({
    container: waveRef.value,
    url: props.audioUrl,
    height: 16,
    barWidth: 3,
    barGap: 2,
    barRadius: 999,
    cursorWidth: 0,
    normalize: true,
    interact: true,
    dragToSeek: true,
    waveColor: '#D0D7E2',
    progressColor: '#2AABEE',
  })

  wavesurfer.on('ready', () => {
    duration.value = wavesurfer?.getDuration() || 0
  })

  wavesurfer.on('play', () => {
    isPlaying.value = true
  })

  wavesurfer.on('pause', () => {
    isPlaying.value = false
  })

  wavesurfer.on('timeupdate', (time) => {
    currentTime.value = time
  })

  wavesurfer.on('finish', () => {
    isPlaying.value = false
    currentTime.value = 0
    if (wavesurfer) clearActiveWaveSurfer(wavesurfer)
  })
})

onBeforeUnmount(() => {
  if (wavesurfer) {
    clearActiveWaveSurfer(wavesurfer)
    wavesurfer.destroy()
  }
})
</script>

<template>
  <div class="voice-message" @click="(e: any) => {togglePlay(e)}">
    <button class="play-btn">
      <Stop v-if="isPlaying" :size="16"/>
      <Play v-else :size="16"/>
    </button>

    <div class="wave-container" ref="waveRef"></div>

    <span class="time">{{ formattedTime }}</span>
  </div>
</template>

<style>
.voice-message {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 250px;
  padding: 6px 12px 6px 6px;
  border-radius: 14px;
  background: var(--color-bg-secondary);
  cursor: pointer;

  .play-btn {
    width: 26px;
    height: 26px;
    border: none;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 11px;
    background: var(--color-background);
    color: var(--color-label);
    cursor: pointer;
    font-size: 14px;
    flex-shrink: 0;
  }

  .wave-container {
    flex: 1;
    min-width: 0;

    & > div {
      background: var(--color-bg-secondary);
    }
  }

  .time {
    font-size: 12px;
    line-height: 14px;
    font-weight: 600;
    background: transparent;
    color: var(--color-label);
    min-width: 42px;
    text-align: left;
  }
}
</style>